import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import NeighborhoodSelect from "@/components/NeighborhoodSelect";

const HERO_IMG =
  "https://images.pexels.com/photos/5638604/pexels-photo-5638604.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Register() {
  const { register, startGoogle, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", neighborhood: "Kilimani" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success(`Karibu Jirani, ${form.name.split(" ")[0]}!`);
      navigate("/feed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="mx-auto grid min-h-[calc(100vh-80px)] max-w-7xl grid-cols-1 items-center gap-12 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:px-8">
      <div>
        <div className="mx-auto max-w-md">
          <div className="jirani-eyebrow">Join your community</div>
          <h1 className="mt-2 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
            Create your Jirani account
          </h1>
          <p className="mt-3 text-[#4A4A4A]">Say hi, look out for neighbors, and support local.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name" data-testid="reg-name-input" required
                value={form.name} onChange={set("name")}
                className="mt-1 h-12 rounded-xl border-black/10 bg-white"
                placeholder="Aisha Karanja"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" data-testid="reg-email-input" type="email" required
                value={form.email} onChange={set("email")}
                className="mt-1 h-12 rounded-xl border-black/10 bg-white"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="neighborhood">Neighbourhood</Label>
              <div className="mt-1">
                <NeighborhoodSelect
                  id="neighborhood"
                  testId="reg-neighborhood-input"
                  value={form.neighborhood}
                  onChange={(v) => setForm((f) => ({ ...f, neighborhood: v }))}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" data-testid="reg-password-input" type="password" required minLength={6}
                value={form.password} onChange={set("password")}
                className="mt-1 h-12 rounded-xl border-black/10 bg-white"
                placeholder="At least 6 characters"
              />
            </div>

            <Button
              data-testid="reg-submit-btn"
              type="submit" disabled={busy}
              className="jirani-pill h-12 w-full rounded-full bg-[#D95D39] text-base font-semibold text-white hover:bg-[#c25333]"
            >
              <UserPlus className="mr-2 h-4 w-4" /> {busy ? "Creating..." : "Create account"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-black/10" />
            <span className="text-xs uppercase tracking-widest text-[#2E473B]/70">or</span>
            <div className="h-px flex-1 bg-black/10" />
          </div>

          <Button
            data-testid="reg-google-btn"
            type="button" variant="outline" onClick={startGoogle}
            className="jirani-pill h-12 w-full rounded-full border-black/10 bg-white text-[#1A1A1A] hover:bg-[#F9F7F4]"
          >
            <svg className="mr-3 h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M23.49 12.27c0-.85-.08-1.66-.22-2.44H12v4.63h6.44c-.28 1.5-1.12 2.77-2.4 3.63v3.02h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.88-3.02c-1.07.72-2.44 1.15-4.06 1.15-3.12 0-5.77-2.11-6.72-4.95H1.29v3.11C3.26 21.3 7.31 24 12 24z" />
              <path fill="#FBBC05" d="M5.28 14.28c-.24-.72-.38-1.49-.38-2.28s.14-1.56.38-2.28V6.61H1.29A11.99 11.99 0 0 0 0 12c0 1.94.46 3.78 1.29 5.39l3.99-3.11z" />
              <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.6 4.6 1.79l3.45-3.45C17.96 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.11C6.23 6.88 8.88 4.77 12 4.77z" />
            </svg>
            Sign up with Google
          </Button>

          <p className="mt-6 text-sm text-[#4A4A4A]">
            Already a jirani?{" "}
            <Link to="/login" data-testid="link-login" className="font-semibold text-[#D95D39] hover:underline">
              Log in here
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="relative overflow-hidden rounded-[28px] border border-black/5 bg-white shadow-[0_30px_60px_-30px_rgba(46,71,59,0.35)]">
          <img src={HERO_IMG} alt="Neighbors" className="h-[520px] w-full object-cover" />
          <div className="absolute inset-x-6 bottom-6 rounded-2xl bg-white/85 p-4 backdrop-blur">
            <div className="jirani-eyebrow">Karibu!</div>
            <div className="mt-1 text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
              You're going to love it here.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
